import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { Search, X } from 'lucide-react';
import {
  getShippingCities,
  getShippingCountries,
  type ShippingAddressSuggestion,
  type ShippingCountryOption,
} from '@/services/shippingService';
import type { ShippingProviderCode } from '@/services/shippingService';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Switch } from '@/components/ui/switch';
import { buildCountrySelectOption, resolveCountryLocalizedName } from '@/utils/countryLocalization';
import { hasNovapostPresence } from '@/utils/novapost';
import { fetchSavedShippingAddresses, type SavedShippingAddress } from '@/services/orderService';
import { localeAwareSort } from '@tulasihome/shared';
import type { ShippingAddress } from '@/lib/shippingAddress';
import { validateShippingAddress } from '@/lib/shippingAddress';
import type { OrderFulfillmentMode } from '@/features/order/orderSlice';

interface ShippingAddressDialogProps {
  readonly open: boolean;
  readonly initial?: Partial<ShippingAddress>;
  readonly countries?: ReadonlyArray<ShippingCountryOption>;
  readonly provider?: ShippingProviderCode;
  readonly fulfillmentMode?: OrderFulfillmentMode;
  readonly onConfirm: (address: ShippingAddress) => void;
  readonly onPickupConfirm?: (address: ShippingAddress) => void;
  readonly onCancel: () => void;
}

const fallbackCountries: ReadonlyArray<ShippingCountryOption> = [
  { code: 'UA', label: 'Ukraine', emoji: '🇺🇦' },
  { code: 'US', label: 'United States', emoji: '🇺🇸' },
  { code: 'GB', label: 'United Kingdom', emoji: '🇬🇧' },
  { code: 'DE', label: 'Germany', emoji: '🇩🇪' },
];

const fieldClass =
  'w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500';

const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

const errorClass = 'text-xs text-red-600 mt-1';
const requiresNovapostSettlementSelection = (countryCode: string): boolean =>
  hasNovapostPresence(countryCode);
const normalizeSuggestionName = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');

export const ShippingAddressDialog: React.FC<ShippingAddressDialogProps> = ({
  open,
  initial,
  countries,
  provider,
  fulfillmentMode: initialFulfillmentMode = 'SHIPPING',
  onConfirm,
  onPickupConfirm,
  onCancel,
}) => {
  const { t, i18n } = useTranslation();
  const resolvedInitialCountryCode = initial?.countryCode || countries?.[0]?.code || fallbackCountries[0]?.code || 'UA';
  const [dynamicCountries, setDynamicCountries] = useState<ReadonlyArray<ShippingCountryOption>>(countries ?? fallbackCountries);
  const countryOptions = useMemo(() => {
    return (countries && countries.length > 0) ? countries : dynamicCountries;
  }, [countries, dynamicCountries]);

  const countrySelectOptions = useMemo(() => {
    const locale = i18n.language;
    const enriched = countryOptions.map((country) => {
      const englishName = country.label?.trim() || country.code;
      const baseCountry = { id: country.code, englishName } as const;
      const localizedName = resolveCountryLocalizedName(baseCountry, locale, t);
      const option = buildCountrySelectOption(baseCountry, locale, t);
      return {
        country,
        localizedName,
        option,
      };
    });

    const sorted = localeAwareSort(enriched, i18n.language, (entry) => entry.localizedName);

    return sorted.map(({ country, option }) => ({
      value: option.value,
      label: option.label,
      emoji: country.emoji ?? option.emoji,
    }));
  }, [countryOptions, i18n.language, t]);

  const [countryCode, setCountryCode] = useState(resolvedInitialCountryCode);
  const [fulfillmentMode, setFulfillmentMode] = useState<OrderFulfillmentMode>(initialFulfillmentMode);
  const [firstName, setFirstName] = useState(initial?.firstName || '');
  const [lastName, setLastName] = useState(initial?.lastName || '');
  const [phone, setPhone] = useState(initial?.phone || '');
  const [street1, setStreet1] = useState(initial?.street1 || '');
  const [street2, setStreet2] = useState(initial?.street2 || '');
  const [city, setCity] = useState(initial?.city || '');
  const [novaposhtaCityRef, setNovaposhtaCityRef] = useState(initial?.novaposhtaCityRef || '');
  const [region, setRegion] = useState(initial?.region || '');
  const [postalCode, setPostalCode] = useState(initial?.postalCode || '');
  const [setAsDefault, setSetAsDefault] = useState<boolean>(initial?.setAsDefault ?? true);
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [savedAddresses, setSavedAddresses] = useState<ReadonlyArray<SavedShippingAddress>>([]);
  const [isSavedAddressesLoading, setIsSavedAddressesLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const cityInputRef = useRef<HTMLInputElement | null>(null);
  const citySuggestionsRef = useRef<HTMLDivElement | null>(null);
  const lastCitySearchKeyRef = useRef<string | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [citySuggestions, setCitySuggestions] = useState<ReadonlyArray<ShippingAddressSuggestion>>([]);
  const [isCitySuggestionsOpen, setIsCitySuggestionsOpen] = useState(false);
  const [isCitySuggestionsLoading, setIsCitySuggestionsLoading] = useState(false);
  const [isValidatingCitySelection, setIsValidatingCitySelection] = useState(false);

  const requiresNovaposhtaCitySelection = requiresNovapostSettlementSelection(countryCode);

  useEffect(() => {
    if (!open) return;
    setCountryCode(resolvedInitialCountryCode);
    setFulfillmentMode(initialFulfillmentMode);
    setFirstName(initial?.firstName || '');
    setLastName(initial?.lastName || '');
    setPhone(initial?.phone || '');
    setStreet1(initial?.street1 || '');
    setStreet2(initial?.street2 || '');
    setCity(initial?.city || '');
    setNovaposhtaCityRef(initial?.novaposhtaCityRef || '');
    setRegion(initial?.region || '');
    setPostalCode(initial?.postalCode || '');
    setSetAsDefault(initial?.setAsDefault ?? true);
    setErrors({});
    setSearchVisible(false);
    setSearchQuery('');
    setCitySuggestions([]);
    setIsCitySuggestionsOpen(false);
  }, [
    open,
    initial?.countryCode,
    initial?.firstName,
    initial?.lastName,
    initial?.phone,
    initial?.street1,
    initial?.street2,
    initial?.city,
    initial?.novaposhtaCityRef,
    initial?.region,
    initial?.postalCode,
    initial?.setAsDefault,
    initialFulfillmentMode,
    resolvedInitialCountryCode,
  ]);

  useEffect(() => {
    if (countries && countries.length > 0) {
      setDynamicCountries(countries);
    }
  }, [countries]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const providerCode: ShippingProviderCode = provider ?? 'UKRPOSHTA';

    let cancelled = false;
    (async () => {
      try {
        const fetched = await getShippingCountries(providerCode);
        if (!cancelled && fetched && fetched.length > 0) {
          setDynamicCountries(fetched);
          if (!initial?.countryCode) {
            setCountryCode((current) => current || fetched[0]?.code || 'UA');
          }
        } else if (!cancelled) {
          setDynamicCountries((prev) => (prev.length > 0 ? prev : fallbackCountries));
        }
      } catch (error) {
        if (!cancelled) {
          setDynamicCountries((prev) => (prev.length > 0 ? prev : fallbackCountries));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, provider, initial?.countryCode]);

  useEffect(() => {
    if (searchVisible) {
      searchInputRef.current?.focus();
    }
  }, [searchVisible]);

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;
    setIsSavedAddressesLoading(true);
    void fetchSavedShippingAddresses()
      .then((addresses) => {
        if (!cancelled) {
          setSavedAddresses(addresses);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSavedAddresses([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSavedAddressesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!requiresNovaposhtaCitySelection) {
      setNovaposhtaCityRef('');
      setCitySuggestions([]);
      setIsCitySuggestionsOpen(false);
      lastCitySearchKeyRef.current = null;
    }
  }, [requiresNovaposhtaCitySelection]);

  useEffect(() => {
    if (!open || !requiresNovaposhtaCitySelection) {
      return;
    }

    const query = city.trim();
    if (query.length < 2) {
      setCitySuggestions([]);
      setIsCitySuggestionsLoading(false);
      lastCitySearchKeyRef.current = null;
      return;
    }

    const requestKey = `${countryCode.trim().toUpperCase()}:${query.toLowerCase()}`;
    if (lastCitySearchKeyRef.current === requestKey) {
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setIsCitySuggestionsLoading(true);
    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          lastCitySearchKeyRef.current = requestKey;
          const suggestions = await getShippingCities('NOVAPOST', {
            countryCode,
            query,
            signal: controller.signal,
          });
          if (!cancelled) {
            setCitySuggestions(suggestions);
            setIsCitySuggestionsOpen(true);
          }
        } catch (error) {
          if (controller.signal.aborted || (isAxiosError(error) && error.code === 'ERR_CANCELED')) {
            return;
          }
          if (!cancelled) {
            lastCitySearchKeyRef.current = null;
            setCitySuggestions([]);
          }
        } finally {
          if (!cancelled) {
            setIsCitySuggestionsLoading(false);
          }
        }
      })();
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [city, countryCode, open, requiresNovaposhtaCitySelection]);

  useEffect(() => {
    if (!isCitySuggestionsOpen) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (
        target
        && citySuggestionsRef.current
        && !citySuggestionsRef.current.contains(target)
        && cityInputRef.current
        && !cityInputRef.current.contains(target)
      ) {
        setIsCitySuggestionsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isCitySuggestionsOpen]);

  const filteredSavedAddresses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return savedAddresses;
    }

    return savedAddresses.filter((address) => {
      const haystack = [
        address.firstName,
        address.lastName,
        address.phone,
        address.countryCode,
        address.region,
        address.city,
        address.postalCode,
        address.street1,
        address.street2 ?? '',
      ].join(' ').toLowerCase();
      return haystack.includes(query);
    });
  }, [savedAddresses, searchQuery]);

  const visibleSavedAddresses = useMemo(() => {
    if (searchQuery.trim().length > 0) {
      return filteredSavedAddresses;
    }
    return filteredSavedAddresses.slice(0, 3);
  }, [filteredSavedAddresses, searchQuery]);

  const hiddenSavedAddressCount = searchQuery.trim().length > 0
    ? 0
    : Math.max(0, filteredSavedAddresses.length - visibleSavedAddresses.length);

  const validate = (): boolean => {
    const errorsNs = 'shippingAddressDialog.errors';
    const next = validateShippingAddress(
      {
        countryCode,
        firstName,
        lastName,
        phone,
        street1,
        street2,
        city,
        novaposhtaCityRef,
        region,
        postalCode,
      },
      {
        countryCode: t(`${errorsNs}.countryCode`, 'Select a valid country'),
        firstName: t(`${errorsNs}.firstName`),
        lastName: t(`${errorsNs}.lastName`),
        phone: t(`${errorsNs}.phone`),
        street1: t(`${errorsNs}.street1`),
        poBoxStreet: t(`${errorsNs}.poBoxStreet`, 'PO Box is allowed, but please also add the full street and house number.'),
        city: t(`${errorsNs}.city`),
        novaposhtaCitySelection: t('shippingAddressDialog.selectCityFromSuggestions', 'Select a city from Nova Post suggestions'),
        region: t(`${errorsNs}.region`),
        postalCode: t(`${errorsNs}.postalCode`),
      },
      {
        requiresNovaposhtaCitySelection,
      },
    );
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateNovapostCitySelection = async (): Promise<boolean> => {
    if (!requiresNovaposhtaCitySelection || !novaposhtaCityRef.trim() || city.trim().length < 2) {
      return true;
    }

    setIsValidatingCitySelection(true);

    try {
      const suggestions = await getShippingCities('NOVAPOST', {
        countryCode,
        query: city.trim(),
      });

      const normalizedCity = normalizeSuggestionName(city);
      const matchedSuggestion = suggestions.find((suggestion) => {
        const suggestionLabel = suggestion.label.split(',')[0]?.trim() || suggestion.label.trim();
        return suggestion.id === novaposhtaCityRef
          && normalizeSuggestionName(suggestionLabel) === normalizedCity;
      });

      if (matchedSuggestion) {
        return true;
      }

      setNovaposhtaCityRef('');
      setCitySuggestions(suggestions);
      setIsCitySuggestionsOpen(true);
      setErrors((current) => ({
        ...current,
        city: t('shippingAddressDialog.selectCityFromSuggestions', 'Select a city from Nova Post suggestions'),
      }));
      return false;
    } catch {
      setErrors((current) => ({
        ...current,
        city: t('shippingAddressDialog.cityValidationFailed', 'Could not verify the city. Try selecting it again.'),
      }));
      return false;
    } finally {
      setIsValidatingCitySelection(false);
    }
  };

  const onSubmit = async () => {
    if (fulfillmentMode === 'PICKUP') {
      const nextErrors: Record<string, string> = {};
      if (!firstName.trim()) {
        nextErrors.firstName = t('shippingAddressDialog.errors.firstName');
      }
      if (!phone.trim()) {
        nextErrors.phone = t('shippingAddressDialog.errors.phone');
      }
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) {
        return;
      }

      onPickupConfirm?.({
        countryCode: initial?.countryCode || countryCode || '',
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        street1: initial?.street1 || '',
        street2: initial?.street2 || undefined,
        city: initial?.city || '',
        novaposhtaCityRef: initial?.novaposhtaCityRef || undefined,
        region: initial?.region || '',
        postalCode: initial?.postalCode || '',
        setAsDefault: initial?.setAsDefault ?? false,
      });
      return;
    }

    if (!validate()) return;
    if (!(await validateNovapostCitySelection())) return;

    onConfirm({
      countryCode,
      firstName,
      lastName,
      phone,
      street1,
      street2: street2 || undefined,
      city,
      novaposhtaCityRef: novaposhtaCityRef || undefined,
      region,
      postalCode,
      setAsDefault,
    });
  };

  const handleCityInputChange = (value: string) => {
    setCity(value);
    if (requiresNovaposhtaCitySelection) {
      setNovaposhtaCityRef('');
      setIsCitySuggestionsOpen(true);
    }
  };

  const handleCitySuggestionSelect = (suggestion: ShippingAddressSuggestion) => {
    const primaryCityName = suggestion.label.split(',')[0]?.trim() || suggestion.label.trim();
    setCity(primaryCityName);
    setNovaposhtaCityRef(suggestion.id);
    setErrors((current) => {
      if (!current.city) {
        return current;
      }
      const { city: _city, ...rest } = current;
      return rest;
    });
    setIsCitySuggestionsOpen(false);
  };

  const handleSavedAddressSelect = (address: SavedShippingAddress) => {
    setCountryCode(address.countryCode);
    setFirstName(address.firstName);
    setLastName(address.lastName);
    setPhone(address.phone);
    setStreet1(address.street1);
    setStreet2(address.street2 ?? '');
    setCity(address.city);
    setNovaposhtaCityRef(address.novaposhtaCityRef ?? '');
    setRegion(address.region);
    setPostalCode(address.postalCode);
    setSetAsDefault(Boolean(address.setAsDefault));
    setErrors({});
    setSearchVisible(false);
    setSearchQuery('');
    setCitySuggestions([]);
    setIsCitySuggestionsOpen(false);
    lastCitySearchKeyRef.current = null;
  };

  const handleCountryChange = (nextCountryCode: string) => {
    if (nextCountryCode === countryCode) {
      return;
    }

    setCountryCode(nextCountryCode);
    setCity('');
    setNovaposhtaCityRef('');
    setCitySuggestions([]);
    setIsCitySuggestionsOpen(false);
    lastCitySearchKeyRef.current = null;
    setErrors((current) => {
      const nextErrors = { ...current };
      delete nextErrors.city;
      return nextErrors;
    });
  };

  if (!open) return null;

  return (
    <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-sm w-full max-w-3xl p-6 border border-gray-200 dark:border-gray-700">
      <div className="mb-4 flex items-start justify-between gap-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('shippingAddressDialog.title')}
        </h3>
        <div className="flex min-w-0 items-center gap-3 rounded-full border border-gray-200 bg-gray-50/80 px-3 py-2 text-xs text-gray-600 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-300">
          <span className={fulfillmentMode === 'SHIPPING' ? 'font-medium text-gray-900 dark:text-gray-100' : ''}>
            {t('orderSummary.fulfillmentShipping', 'Delivery')}
          </span>
          <Switch
            checked={fulfillmentMode === 'PICKUP'}
            onCheckedChange={(checked) => setFulfillmentMode(checked ? 'PICKUP' : 'SHIPPING')}
            aria-label={t('shippingAddressDialog.fulfillmentLabel', 'Order receiving method')}
          />
          <span className={fulfillmentMode === 'PICKUP' ? 'font-medium text-gray-900 dark:text-gray-100' : ''}>
            {t('orderSummary.fulfillmentPickup', 'Self pickup')}
          </span>
        </div>
      </div>

      <div className="space-y-6">
        {fulfillmentMode === 'PICKUP' ? (
          <div className="space-y-4">
            <p className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
              {t('orderSummary.pickupHelper', 'You will collect the printed art yourself. Delivery fee is not charged.')}
            </p>
            <div>
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                {t('shippingAddressDialog.contactInfoLabel')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <input
                    className={fieldClass}
                    placeholder={t('shippingAddressDialog.firstNamePlaceholder')}
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      if (errors.firstName) {
                        setErrors((current) => {
                          const { firstName: _firstName, ...rest } = current;
                          return rest;
                        });
                      }
                    }}
                  />
                  {errors.firstName && <div className={errorClass}>{errors.firstName}</div>}
                </div>
                <div>
                  <input
                    className={fieldClass}
                    placeholder={t('shippingAddressDialog.lastNamePlaceholder')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div>
                  <input
                    className={fieldClass}
                    placeholder={t('shippingAddressDialog.phonePlaceholder')}
                    value={phone}
                    onChange={(e) => {
                      setPhone(e.target.value);
                      if (errors.phone) {
                        setErrors((current) => {
                          const { phone: _phone, ...rest } = current;
                          return rest;
                        });
                      }
                    }}
                  />
                  {errors.phone && <div className={errorClass}>{errors.phone}</div>}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {fulfillmentMode === 'SHIPPING' ? (
          <>
        <div>
          <label className={labelClass}>{t('shippingAddressDialog.countryRegionLabel')}</label>
          <SearchableSelect
            options={countrySelectOptions}
            value={countryCode}
            onChange={handleCountryChange}
            placeholder={t('shippingAddressDialog.selectCountryPlaceholder', 'Select a country')}
            searchPlaceholder={t('shippingAddressDialog.countrySearchPlaceholder', 'Search country')}
            noMatchesLabel={t('shippingAddressDialog.noCountryMatch', 'No matches')}
            className={`${fieldClass} flex items-center justify-between`}
            menuClassName="absolute z-50 mt-2 w-full rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg"
            inputClassName={`${fieldClass} h-8`}
            optionClassName="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
            selectedOptionClassName="w-full text-left px-3 py-2 text-sm text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
            {t('shippingAddressDialog.contactInfoLabel')}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <input
                className={fieldClass}
                placeholder={t('shippingAddressDialog.firstNamePlaceholder')}
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  if (errors.firstName) {
                    setErrors((current) => {
                      const { firstName: _firstName, ...rest } = current;
                      return rest;
                    });
                  }
                }}
              />
              {errors.firstName && <div className={errorClass}>{errors.firstName}</div>}
            </div>
            <div>
              <input
                className={fieldClass}
                placeholder={t('shippingAddressDialog.lastNamePlaceholder')}
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  if (errors.lastName) {
                    setErrors((current) => {
                      const { lastName: _lastName, ...rest } = current;
                      return rest;
                    });
                  }
                }}
              />
              {errors.lastName && <div className={errorClass}>{errors.lastName}</div>}
            </div>
            <div>
              <input
                className={fieldClass}
                placeholder={t('shippingAddressDialog.phonePlaceholder')}
                value={phone}
                onChange={(e) => {
                  setPhone(e.target.value);
                  if (errors.phone) {
                    setErrors((current) => {
                      const { phone: _phone, ...rest } = current;
                      return rest;
                    });
                  }
                }}
              />
              {errors.phone && <div className={errorClass}>{errors.phone}</div>}
            </div>
          </div>
        </div>

        <div>
          <div className="space-y-3">
            <div className="flex justify-end">
              {!searchVisible && (
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 text-gray-600 hover:text-gray-900 dark:border-gray-600 dark:text-gray-300 dark:hover:text-white"
                  onClick={() => setSearchVisible(true)}
                  aria-label={t('shippingAddressDialog.searchButtonLabel', 'Search address fields')}
                  title={t('shippingAddressDialog.searchButtonLabel', 'Search address fields')}
                >
                  <Search className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
            {searchVisible && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" aria-hidden="true" />
                <input
                  ref={searchInputRef}
                  className={`${fieldClass} pl-10 pr-10`}
                  placeholder={t('shippingAddressDialog.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800"
                  onClick={() => setSearchVisible(false)}
                  aria-label={t('shippingAddressDialog.hideSearchButtonLabel', 'Hide search field')}
                  title={t('shippingAddressDialog.hideSearchButtonLabel', 'Hide search field')}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            )}
            {searchVisible ? (
              <div className="rounded-lg border border-gray-200 bg-gray-50/70 dark:border-gray-700 dark:bg-gray-800/50">
                {isSavedAddressesLoading ? (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {t('shippingAddressDialog.savedAddressesLoading', 'Loading saved addresses...')}
                  </div>
                ) : visibleSavedAddresses.length > 0 ? (
                  <div className="max-h-56 overflow-auto py-2">
                    {visibleSavedAddresses.map((address) => (
                      <button
                        key={address.id}
                        type="button"
                        className="flex w-full flex-col items-start gap-1 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-gray-700/70"
                        onClick={() => handleSavedAddressSelect(address)}
                      >
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {[address.firstName, address.lastName].filter(Boolean).join(' ')}
                          {address.setAsDefault ? ' · ' + t('shippingAddressDialog.defaultAddressBadge', 'Default') : ''}
                        </span>
                        <span className="text-xs text-gray-600 dark:text-gray-300">
                          {[address.street1, address.street2, address.city, address.region, address.postalCode, address.countryCode]
                            .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
                            .join(', ')}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{address.phone}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                    {savedAddresses.length === 0
                      ? t('shippingAddressDialog.noSavedAddresses', 'No saved addresses yet.')
                      : t('shippingAddressDialog.noSavedAddressMatch', 'No saved addresses match your search.')}
                  </div>
                )}
                {hiddenSavedAddressCount > 0 ? (
                  <div className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
                    {t('shippingAddressDialog.moreSavedAddressesHint', '{{count}} more saved addresses. Start typing to search all.', { count: hiddenSavedAddressCount })}
                  </div>
                ) : null}
              </div>
            ) : null}
            <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200">
              {t('shippingAddressDialog.addressLabel')}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <input
                  className={fieldClass}
                  placeholder={t('shippingAddressDialog.street1Placeholder')}
                  value={street1}
                  onChange={(e) => {
                    setStreet1(e.target.value);
                    if (errors.street1) {
                      setErrors((current) => {
                        const { street1: _street1, ...rest } = current;
                        return rest;
                      });
                    }
                  }}
                />
                {errors.street1 && <div className={errorClass}>{errors.street1}</div>}
              </div>
              <div>
                <input
                  className={fieldClass}
                  placeholder={t('shippingAddressDialog.street2Placeholder')}
                  value={street2}
                  onChange={(e) => setStreet2(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <input
                  className={fieldClass}
                  placeholder={t('shippingAddressDialog.regionPlaceholder')}
                  value={region}
                  onChange={(e) => {
                    setRegion(e.target.value);
                    if (errors.region) {
                      setErrors((current) => {
                        const { region: _region, ...rest } = current;
                        return rest;
                      });
                    }
                  }}
                />
                {errors.region && <div className={errorClass}>{errors.region}</div>}
              </div>
              <div>
                <div className="relative">
                  <input
                    ref={cityInputRef}
                    className={fieldClass}
                    placeholder={t('shippingAddressDialog.cityPlaceholder')}
                    value={city}
                    onChange={(e) => handleCityInputChange(e.target.value)}
                    onFocus={() => {
                      if (requiresNovaposhtaCitySelection && citySuggestions.length > 0) {
                        setIsCitySuggestionsOpen(true);
                      }
                    }}
                  />
                  {requiresNovaposhtaCitySelection && isCitySuggestionsOpen && (citySuggestions.length > 0 || isCitySuggestionsLoading) && (
                    <div
                      ref={citySuggestionsRef}
                      className="absolute z-50 mt-2 max-h-56 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
                    >
                      {isCitySuggestionsLoading && (
                        <div className="px-3 py-2 text-sm text-gray-500">
                          {t('shippingAddressDialog.loadingCities', 'Loading cities...')}
                        </div>
                      )}
                      {!isCitySuggestionsLoading && citySuggestions.map((suggestion) => (
                        <button
                          key={suggestion.id}
                          type="button"
                          className="block w-full px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleCitySuggestionSelect(suggestion)}
                        >
                          {suggestion.fullName ?? suggestion.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {errors.city && <div className={errorClass}>{errors.city}</div>}
              </div>
              <div>
                <input
                  className={fieldClass}
                  placeholder={t('shippingAddressDialog.postalCodePlaceholder')}
                  value={postalCode}
                  onChange={(e) => {
                    setPostalCode(e.target.value);
                    if (errors.postalCode) {
                      setErrors((current) => {
                        const { postalCode: _postalCode, ...rest } = current;
                        return rest;
                      });
                    }
                  }}
                />
                {errors.postalCode && <div className={errorClass}>{errors.postalCode}</div>}
              </div>
            </div>
          </div>
        </div>

        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={setAsDefault}
            onChange={(e) => setSetAsDefault(e.target.checked)}
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('shippingAddressDialog.setDefaultCheckbox')}
          </span>
        </label>
          </>
        ) : null}

        <div className="flex items-center justify-start gap-3 pt-2">
          <button
            type="button"
            className="rounded-md bg-rose-600 text-white px-4 py-2 text-sm font-semibold hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onSubmit}
            disabled={isValidatingCitySelection}
          >
            {fulfillmentMode === 'PICKUP'
              ? t('common.continue', 'Continue')
              : isValidatingCitySelection
                ? t('shippingAddressDialog.validatingCity', 'Validating city...')
                : t('common.confirm')}
          </button>
          <button
            type="button"
            className="rounded-md border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm text-gray-800 dark:text-gray-100"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
;

export default ShippingAddressDialog;
